import React, { useEffect } from "react";
import styled from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import mixpanel from "mixpanel-browser";
import { AuthoritiesEnum, CRUDActionEnum, RoutesEnum, StatusStateEnum, TopicStatusEnum, TopicVisibilityEnum } from "~/types/enums";
import Modal from "~/components-shared/Modal";
import topicSchema from "~/forms/TopicForm/topicSchema";
import topicActionCreators from "~/store/topics/actions";
import topicSelectors from "~/store/topics/selectors";
import TopicForm from "~/forms/TopicForm";
import toastsActionCreators from "~/store/toasts/actions";
import { useHistory } from "react-router-dom";
import { Alert } from "~/components-shared";
import useAbacExt from "~/hooks/abac";
import moment from "moment/moment";
import { helpers } from "~/lib/helpers";
const Sticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
`;
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
`;
const TopicUpdateModal = () => {
  const {
    t
  } = useTranslation();
  const topic = useSelector(topicSelectors.getSelected);
  const topicId = topic?.id || "";
  const status = useSelector(topicSelectors.getStatusByTopic(topicId));
  const error = useSelector(topicSelectors.getErrorByTopic(topicId));
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    userHasOneOf
  } = useAbacExt();
  const actions = [];
  const defaultValues = {
    name: topic?.name || "",
    status: topic?.status || TopicStatusEnum.ACTIVE,
    description: topic?.description || "",
    keywords: topic?.keywords || "",
    visibility: topic?.visibility || TopicVisibilityEnum.PRIVATE,
    startDate: topic?.startDate || moment().toISOString(),
    ownerId: topic?.owner?.id || "",
    memberIds: topic?.members?.map(member => member.id) || [],
    sources: topic?.sources || [],
    trigger: topic?.trigger || "",
    settings: {
      slackChannels: topic?.settings?.slackChannels,
      slackDirectChannels: topic?.settings?.slackDirectChannels,
      jiraProjects: topic?.settings?.jiraProjects,
      linearTeams: topic?.settings?.linearTeams,
      asanaProjects: topic?.settings?.asanaProjects,
      githubRepositories: topic?.settings?.githubRepositories,
      salesforceOpportunities: topic?.settings?.salesforceOpportunities,
      salesforceRecords: topic?.settings?.salesforceRecords
    },
    markSlackAiDetections: topic?.markSlackAiDetections || false
  };
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(topicSchema),
    defaultValues
  });
  useEffect(() => {
    if (topic) {
      reset(defaultValues);
    }
  }, [topicId]);
  const onSubmit = handleSubmit(data => {
    if (topicId) {
      mixpanel.track(`Topics Update – Save "${topic?.name}"`, {
        ...helpers.objectDiff(defaultValues, data)
      });
      dispatch(topicActionCreators.updateStarted(topicId, data, _ref => {
        let {
          data: newTopic
        } = _ref;
        if (newTopic) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("TopicUpdateModal.topicUpdated", {
              name: newTopic.name
            }),
            timeout: 10000,
            actions: [{
              icon: "external-link",
              onClick: () => history.push(RoutesEnum.TOPIC.replace(":topicId", newTopic.id))
            }]
          }));
        }
      }));
    }
  });
  const handleCancel = () => {
    dispatch(topicActionCreators.actionChanged(null, null));
    mixpanel.track(`Topics Update – Cancel "${topic?.name}"`);
  };
  const handleDelete = () => {
    dispatch(topicActionCreators.actionChanged(CRUDActionEnum.DELETE, topicId));
    mixpanel.track(`Topics Update – Delete "${topic?.name}"`);
  };
  if (userHasOneOf([AuthoritiesEnum.TOPICS_WRITE], topic?.createdBy.id)) {
    actions.push({
      icon: "bin",
      tip: t("common.actions.delete"),
      onClick: handleDelete
    });
  }
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: t("TopicUpdateModal.updateTopic"),
    onClose: handleCancel,
    fullWidth: true,
    actions: actions,
    maxWidth: "1200px"
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Sticky, null, /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error)), /*#__PURE__*/React.createElement(TopicForm, {
    control: control,
    watch: watch,
    topic: topic,
    setValue: setValue
  })), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: handleCancel,
    rounded: true
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "submit",
    maxWidth: "230px",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("TopicUpdateModal.saveTopic")))));
};
export default TopicUpdateModal;